<template>
    <page-shell title="Move Booking">
        <spinner :loading="loading" text="Moving booking..."></spinner>
        <el-form class="booking-form" v-if="booking" label-width="160px" :onSubmit="moveBooking">
            <template>
                <div class="form-wrapper">
                    <span>
                        <div class="info-wrapper">
                            <span class="info-label">Booking Number: </span>
                            <span class="info-value">{{
                                booking.code
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Email: </span>
                            <span class="info-value">{{
                                booking.client.email
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Phone number: </span>
                            <span class="info-value">{{
                                booking.client.phone_number
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Location: </span>
                            <span class="info-value">{{
                                booking.xcube.location.name
                                }}</span>
                        </div>

                        <div class="info-wrapper">
                            <span class="info-label">X-Cube Name: </span>
                            <span class="info-value">{{
                                booking.xcube.name
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Team name</span>
                            <span class="info-value">{{
                                booking.team.name
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Player Count</span>
                            <span class="info-value">{{
                                booking.player_count
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Language</span>
                            <span class="info-value">{{
                                booking.language
                                }}</span>
                        </div>
                        <div class="info-wrapper">
                            <span class="info-label">Experience</span>
                            <span class="info-value">{{
                                booking.experience.name
                                }}</span>
                        </div>

                        <el-form-item label="New Event Date">
                            <div class="input-with-text">
                                <date-picker v-model="eventDate" type="date" format="DD-MM-YYYY"
                                    :disabled-date="(date) => date < dayjs().startOf('day').toDate()"
                                    :get-classes="closedDayClass"
                                    placeholder="Select date" required>
                                    <template #footer>
                                        <span>Marked days are outside opening hours.</span>
                                    </template>
                                </date-picker>
                            </div>
                            <span v-if="selectedDayOutside" class="text-next-to-input">This day is outside the location's opening hours. Moving the booking here will be refused.</span>
                        </el-form-item>
                        <el-form-item label="New Event Time">
                            <select-time v-model="eventTime" :time-slots="slotsWithStatus"
                                :duration="booking.experience.duration"
                                :has-sync-booking-system="hasSyncBookingSystem"
                                :outside-opening-hours="selectedTimeOutside"
                                :day-outside-opening-hours="selectedDayOutside"
                                outside-opening-hours-text="This time is outside the location's opening hours. Moving the booking here will be refused." />
                            <span class="text-next-to-input">Timezone: {{ locationTimeZone }}</span>
                        </el-form-item>

                        <el-form-item v-if="eventDateTime && locationTimeZone !== userTimeZone" label="Local Time">
                            <span class="text-next-to-input">
                                {{ dayjs(eventDateTime).format('DD-MM-YYYY HH:mm') }}
                                {{ userTimeZone }}
                            </span>
                        </el-form-item>

                        <el-form-item label="">
                            <el-button @click="back">
                                <font-awesome-icon icon="fa-solid fa-arrow-left" />
                            </el-button>
                            <el-button @click="handleMoveBooking">Move</el-button>
                        </el-form-item>
                    </span>
                </div>
            </template>
        </el-form>
    </page-shell>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import DatePicker from "vue2-datepicker";
import SelectTime from "../shared/SelectTime.vue";
import "vue2-datepicker/index.css";
import dayjs from "dayjs";
import { Message } from "element-ui";

import { getLocationSchedule, moveBooking, getTimeSlots } from "../../api/rest";
import { calendarDateOf, dayIsOutsideOpeningHours, timeIsOutsideOpeningHours } from "../../helpers/openingHours";
import { fetchBookingData } from "../../api/graphql/bookings";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";
import { Temporal } from "temporal-polyfill";

const router = useRouter();
const route = useRoute();
const form = ref(null);

const loading = ref(false);
const timeSlots = ref([]);
const schedule = ref(null);

const eventDate = ref(new Date());
const eventTime = ref("");

const booking = ref(null);

const userTimeZone = computed(() => {
    // Get the current timezone name
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezoneName;
});

const locationTimeZone = computed(() => booking.value.xcube.location.time_zone);

const selectedDayOutside = computed(() => {
    if (!schedule.value || !eventDate.value || !booking.value) {
        return false;
    }
    return dayIsOutsideOpeningHours(schedule.value, calendarDateOf(eventDate.value), locationTimeZone.value);
});

const selectedTimeOutside = computed(() => {
    if (selectedDayOutside.value || !schedule.value || !eventTime.value || !booking.value) {
        return false;
    }
    return timeIsOutsideOpeningHours(schedule.value, {
        date: calendarDateOf(eventDate.value),
        time: eventTime.value,
        duration: booking.value.experience.duration,
        timeZone: locationTimeZone.value,
    });
});

const closedDayClass = (date) => {
    if (!schedule.value || !booking.value) {
        return [];
    }
    return dayIsOutsideOpeningHours(schedule.value, calendarDateOf(date), locationTimeZone.value)
        ? ["hub-closed-day"]
        : [];
};

const hasSyncBookingSystem = computed(() => {
    return ['xpay', 'ireserve'].includes(booking.value.xcube.location.booking_system);
});

const slotsWithStatus = computed(() => timeSlots.value.map((slot) => {
    let status = "available";
    const [hours, minutes] = slot.time.split(":").map(Number);
    const slotDateTime = dayjs(eventDate.value).tz(locationTimeZone.value, true)
        .hour(hours)
        .minute(minutes)
        .second(0);
    if (slotDateTime.isBefore(dayjs())) {
        status = "past";
    } else if (slot.available === 0) {
        status = "unavailable";
    } else if (slot.time === eventTime.value) {
        status = "selected";
    }
    return {
        time: slot.time,
        status,
    };
}));

const eventDateTime = computed(() => {
    if (eventDate.value && eventTime.value) {
        let realDate = eventDate.value;
        if (eventTime.value > "00:00" && eventTime.value < "02:00") {
            realDate = dayjs(realDate).add(1, "day").toDate();
        }
        const zdt = Temporal.ZonedDateTime.from({
            timeZone: locationTimeZone.value,
            year: realDate.getFullYear(),
            month: realDate.getMonth() + 1,
            day: realDate.getDate(),
            hour: Number.parseInt(eventTime.value.split(":")[0]),
            minute: Number.parseInt(eventTime.value.split(":")[1]),
            second: 0
        });
        return new Date(zdt.epochMilliseconds);
    }
    return null;
});

watch(eventDate, async () => {
    if (booking.value) {
        const { json, status } = await getTimeSlots(
            booking.value.xcube.id,
            dayjs(eventDate.value).format("YYYY-MM-DD"),
            booking.value.experience.duration,
            booking.value.id,
        )
        if (status !== 200) {
            Message.error({
                message: `Error from server : ${json.message}`,
            });
            return;
        }
        timeSlots.value = json;
    }
},
);

const back = () => {
    router.go(-1);
};

const handleMoveBooking = async () => {
    if (dayjs(eventDateTime.value).isBefore(dayjs())) {
        Message.error({
            message: "Date/time should be in the future",
        });
        return;
    }
    console.log("NEW DATE", eventDateTime.value);
    try {
        loading.value = true;
        const { data, status } = await moveBooking(booking.value, eventDateTime.value);
        if (status !== 200) {
            throw new Error(data.message);
        }
        console.log("NEW BOOKING", data);
        loading.value = false;
        Message.success({ message: "Booking has been moved" });
        router.push({
            name: "view-booking",
            params: { id: data.id },
        });
    } catch (e) {
        loading.value = false;
        if (e.message === "conflict") {
            Message.error({
                message: "There is already another booking on this time",
            });
        } else {
            Message.error({
                message: `Error from server : ${e.message}`,
            });
            throw e;
        }
    }
};

onMounted(async () => {
    booking.value = await fetchBookingData(route.params.id);
    try {
        schedule.value = await getLocationSchedule(booking.value.xcube.location.id);
    } catch (error) {
        schedule.value = null;
        Message.error(error.message || "Could not load opening hours");
    }
    eventDate.value = new Date(booking.value.date);
    const bookingDate = dayjs(booking.value.date).tz(locationTimeZone.value);
    const minutes = bookingDate.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    eventTime.value = `${bookingDate.hour().toString().padStart(2,"0")}:${roundedMinutes.toString().padStart(2, "0")}`;
    eventDate.value = new Date(booking.value.date);
});
</script>

<style lang="scss" scoped>
@use "../../colors" as *;

.booking-form {
    box-sizing: border-box;
    width: 100%;
    margin: 0 0 2rem;
}

.text-next-to-input {
    color: $color-primary;
    text-align: left;
    display: block;
}

.input-with-text {
    display: flex;
}

.info-wrapper {
    display: flex;
    padding-bottom: 12px;

    .info-label {
        flex: 0 0 10rem;
        text-align: left;
        padding-right: 12px;
    }

    .info-value {
        text-align: left;
        min-width: 0;
    }
}

.warning {
    color: $color-primary;
    font-size: 0.875rem;
    line-height: 1rem;
}

@media (max-width: 768px) {
    .info-wrapper {
        flex-direction: column;
        gap: 0.15rem;
    }

    .info-wrapper .info-label {
        flex: none;
    }
}
</style>